/**
 * BuildPage - Warband Builder Route
 *
 * Main page for creating/editing a warband:
 * - Wizard and Apprentice with item caps (5/4) and stats
 * - Apprentice stats auto-calculated: Wizard stats -2 (except Move)
 * - Soldiers from official templates (fixed stats)
 * - Spells from official list
 * - Treasury, Vault, and Base
 *
 * Treasury starts at 400gc and auto-deducts when hiring.
 */

import { Button } from '@/components/ui';
import {
  WizardCard,
  SoldierRoster,
  SpellList,
  TreasuryVaultSection,
  BaseSection,
} from '@/components/build';
import { UserPlus, Download, Upload, RotateCcw, Coins } from 'lucide-react';
import {
  useWarbandStore,
  selectWizard,
  selectApprentice,
  selectTreasury,
  APPRENTICE_ITEM_CAP,
  APPRENTICE_COST_GC,
  DEFAULT_APPRENTICE_HEALTH,
  calculateApprenticeStats,
} from '@/store/warbandSlice';
import type { Caster } from '@/store/warbandSlice';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BuildPage() {
  // Store selectors
  const wizard = useWarbandStore(selectWizard);
  const apprentice = useWarbandStore(selectApprentice);
  const treasury = useWarbandStore(selectTreasury);

  // Store actions
  const setWizard = useWarbandStore((state) => state.setWizard);
  const setApprentice = useWarbandStore((state) => state.setApprentice);
  const exportWarband = useWarbandStore((state) => state.exportWarband);
  const load = useWarbandStore((state) => state.load);
  const reset = useWarbandStore((state) => state.reset);

  // Derived values
  const canAffordApprentice = treasury >= APPRENTICE_COST_GC;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleWizardUpdate = (updatedWizard: Caster) => {
    setWizard(updatedWizard);

    // If apprentice exists, update their stats based on wizard's stats
    if (apprentice) {
      const apprenticeStats = calculateApprenticeStats(updatedWizard.stats);
      setApprentice({
        ...apprentice,
        stats: apprenticeStats,
      });
    }
  };

  const handleApprenticeUpdate = (updatedApprentice: Caster) => {
    // Note: Apprentice stats are always derived from wizard, so we only update non-stat fields
    // But if stats were manually edited, we accept them (for flexibility)
    setApprentice(updatedApprentice);
  };

  const handleAddApprentice = () => {
    if (!canAffordApprentice) {
      alert(`Not enough gold to hire apprentice. Need ${APPRENTICE_COST_GC}gc, have ${treasury}gc.`);
      return;
    }

    // Calculate apprentice stats from wizard stats (-2 to all except Move)
    const apprenticeStats = calculateApprenticeStats(wizard.stats);

    const newApprentice: Caster = {
      name: '',
      school: wizard.school,
      level: 0,
      stats: apprenticeStats,
      items: [],
      maxItems: APPRENTICE_ITEM_CAP,
      health: { max: DEFAULT_APPRENTICE_HEALTH, current: DEFAULT_APPRENTICE_HEALTH },
      xp: 0,
    };
    const result = setApprentice(newApprentice);
    if (!result.success) {
      alert(result.message);
    }
  };

  const handleRemoveApprentice = () => {
    setApprentice(undefined);
  };

  const handleExport = () => {
    const data = exportWarband();
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `warband-${wizard.name || 'unnamed'}-v${data.version}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        const data = JSON.parse(text);
        if (data.warband && typeof data.version === 'number') {
          const result = load(data.warband, data.version);
          if (!result.success) {
            alert(`Import failed: ${result.message}`);
          }
        } else {
          alert('Invalid warband file format');
        }
      } catch {
        alert('Failed to parse file');
      }
    };
    input.click();
  };

  const handleReset = () => {
    if (confirm('Are you sure you want to reset the warband? This cannot be undone.')) {
      reset();
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen pb-safe">
      {/* Header with Golden Treasury */}
      <header className="sticky top-0 z-40 border-b border-yellow-500/30 bg-gradient-to-r from-yellow-900/20 via-yellow-800/10 to-yellow-900/20 backdrop-blur supports-[backdrop-filter]:bg-yellow-900/10">
        <div className="container px-4">
          <div className="flex h-14 items-center justify-between">
            {/* Golden Treasury Display */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-yellow-600 to-yellow-500 px-4 py-2 shadow-lg shadow-yellow-500/20">
                <Coins className="h-5 w-5 text-yellow-100" />
                <span className="font-mono text-xl font-bold text-yellow-50">
                  {treasury}
                </span>
                <span className="text-sm font-medium text-yellow-200">gc</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={handleExport} aria-label="Export">
                <Download className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleImport} aria-label="Import">
                <Upload className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleReset} aria-label="Reset">
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container space-y-6 px-4 py-6">
        {/* Wizard Section */}
        <section aria-labelledby="wizard-heading">
          <WizardCard caster={wizard} role="wizard" onUpdate={handleWizardUpdate} />
        </section>

        {/* Apprentice Section */}
        <section aria-labelledby="apprentice-heading">
          {apprentice ? (
            <WizardCard
              caster={apprentice}
              role="apprentice"
              onUpdate={handleApprenticeUpdate}
              onRemove={handleRemoveApprentice}
              wizardStats={wizard.stats}
            />
          ) : (
            <Button
              variant="outline"
              className="w-full border-dashed"
              onClick={handleAddApprentice}
              disabled={!canAffordApprentice}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Hire Apprentice
              <span className="ml-2 rounded bg-yellow-500/20 px-2 py-0.5 text-xs font-medium text-yellow-600 dark:text-yellow-400">
                {APPRENTICE_COST_GC}gc
              </span>
              {!canAffordApprentice && (
                <span className="ml-2 text-xs text-destructive">- Not enough gold</span>
              )}
            </Button>
          )}
        </section>

        {/* Soldiers Section */}
        <section aria-labelledby="soldiers-heading">
          <SoldierRoster />
        </section>

        {/* Spells Section */}
        <section aria-labelledby="spells-heading">
          <SpellList />
        </section>

        {/* Treasury & Vault Section */}
        <section aria-labelledby="treasury-heading">
          <TreasuryVaultSection />
        </section>

        {/* Base Section */}
        <section aria-labelledby="base-heading">
          <BaseSection />
        </section>

        {/* Footer Space for Bottom Nav */}
        <div className="h-20" />
      </main>
    </div>
  );
}
